import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, Trash2, CheckCircle2, DollarSign, Calculator, Delete, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api, formatApiError } from "../lib/api";
import {
  MENU as DEFAULT_MENU,
  CATEGORY_ORDER as DEFAULT_ORDER,
  FLAVOR_OPTIONS as DEFAULT_FLAVORS,
  SODA_OPTIONS as DEFAULT_SODAS,
  DIP_OPTIONS as DEFAULT_DIPS,
  COMBO_PRICE as DEFAULT_COMBO_PRICE,
  money,
} from "../lib/menu";
import ItemDialog from "../components/order/ItemDialog";
import { useCart } from "../context/CartContext";

const centsFromInput = (v) => {
  // Treat input as dollars. "20" -> 2000, "20.5" -> 2050, "20.55" -> 2055.
  const s = String(v).trim().replace(/[^0-9.]/g, "");
  if (!s) return 0;
  const n = Number(s);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
};

export default function Pos() {
  const navigate = useNavigate();
  const { items, addItem, removeItem, clear, totalCents } = useCart();

  const [menu, setMenu] = useState(DEFAULT_MENU);
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [flavors, setFlavors] = useState(DEFAULT_FLAVORS);
  const [sodas, setSodas] = useState(DEFAULT_SODAS);
  const [dips, setDips] = useState(DEFAULT_DIPS);
  const [comboPrice, setComboPrice] = useState(DEFAULT_COMBO_PRICE);
  const [images, setImages] = useState({});
  const [active, setActive] = useState(null);
  const [cat, setCat] = useState(null); // null => show all
  const [tender, setTender] = useState(""); // dollars string
  const [busy, setBusy] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [receipt, setReceipt] = useState(null); // {order_number, total_cents, tender_cents, change_cents}

  // Staff gate: same login as kiosk/KDS
  useEffect(() => {
    if (!localStorage.getItem("kds_token")) {
      navigate("/kds/login?next=/pos");
    }
  }, [navigate]);

  useEffect(() => {
    api.get("/menu").then(({ data }) => {
      if (data?.menu) setMenu(data.menu);
      if (Array.isArray(data?.categoryOrder)) setOrder(data.categoryOrder);
      if (Array.isArray(data?.flavors) && data.flavors.length) setFlavors(data.flavors);
      if (Array.isArray(data?.sodas) && data.sodas.length) setSodas(data.sodas);
      if (Array.isArray(data?.dips) && data.dips.length) setDips(data.dips);
      if (Number.isFinite(data?.comboPrice)) setComboPrice(data.comboPrice);
      if (data?.images) setImages(data.images);
    }).catch(() => {});
  }, []);

  const tenderCents = centsFromInput(tender);
  const change = Math.max(0, tenderCents - totalCents);
  const canCharge = totalCents > 0;

  const exit = () => navigate("/kds");

  const charge = useCallback(async () => {
    if (!canCharge) return;
    setBusy(true);
    try {
      const payload = {
        items: items.map((it) => ({
          name: it.name, quantity: it.quantity, unit_price_cents: it.unit_price_cents, options: it.options || [],
        })),
        tender_cents: tenderCents || totalCents,
      };
      const { data } = await api.post("/pos/orders", payload);
      setReceipt(data);
      clear();
      setTender("");
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem("kds_token"); navigate("/kds/login?next=/pos"); }
      else toast.error(formatApiError(err.response?.data?.detail) || "Could not ring up order");
    } finally { setBusy(false); }
  }, [items, tenderCents, totalCents, canCharge, clear, navigate]);

  const chargeManual = useCallback(async ({ amountCents, tenderCents: t, label }) => {
    setBusy(true);
    try {
      const { data } = await api.post("/pos/orders", {
        items: [],
        custom_amount_cents: amountCents,
        tender_cents: t,
        label: label || "Manual Sale",
      });
      setReceipt(data);
      setManualOpen(false);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem("kds_token"); navigate("/kds/login?next=/pos"); }
      else toast.error(formatApiError(err.response?.data?.detail) || "Could not ring up manual sale");
    } finally { setBusy(false); }
  }, [navigate]);

  const flatItems = order.flatMap((k) => menu[k]?.items || []).filter((it) => it.enabled !== false);
  const visibleItems = cat ? (menu[cat]?.items || []).filter((it) => it.enabled !== false) : flatItems;

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-neon-green/25 h-[64px] px-5 flex items-center justify-between shrink-0">
        <div className="flex items-baseline gap-2">
          <span className="font-heading text-3xl text-neon-green neon-glow-green leading-none">67</span>
          <span className="font-heading text-xl text-neon-yellow leading-none tracking-wide">POS</span>
          <span className="hidden sm:inline font-body text-xs text-neutral-500 uppercase tracking-[0.2em] ml-3">Cash · In-Store</span>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setManualOpen(true)} data-testid="pos-manual-btn" variant="outline" className="border-neon-yellow text-neon-yellow hover:bg-neon-yellow hover:text-black font-heading">
            <Calculator size={16} className="mr-2" /> Manual $
          </Button>
          <Button onClick={exit} data-testid="pos-exit-btn" variant="outline" className="border-neutral-700 text-neutral-400 hover:bg-neutral-900 hover:text-white font-heading">
            <LogOut size={16} className="mr-2" /> Exit
          </Button>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_380px] overflow-hidden">
        {/* Menu grid */}
        <div className="overflow-y-auto p-5">
          {/* Category tabs */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => setCat(null)}
              data-testid="pos-cat-all"
              className={`font-heading text-sm uppercase tracking-wide px-3 py-1.5 border transition-colors ${!cat ? "border-neon-green bg-neon-green text-black" : "border-neon-green/30 text-neon-green hover:bg-neon-green/10"}`}
            >All</button>
            {order.map((k) => menu[k] && (
              <button
                key={k}
                onClick={() => setCat(k)}
                data-testid={`pos-cat-${k}`}
                className={`font-heading text-sm uppercase tracking-wide px-3 py-1.5 border transition-colors ${cat === k ? "border-neon-green bg-neon-green text-black" : "border-neon-green/30 text-neon-green hover:bg-neon-green/10"}`}
              >{menu[k].title}</button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {visibleItems.map((it) => {
              const price = it.variants ? Math.min(...it.variants.map((v) => v.price)) : it.price;
              const soldOut = !!it.sold_out;
              return (
                <button
                  key={it.id}
                  onClick={() => !soldOut && setActive(it)}
                  disabled={soldOut}
                  data-testid={`pos-item-${it.id}`}
                  className={`relative group p-3 text-left transition-colors flex flex-col overflow-hidden border ${
                    soldOut
                      ? "bg-elevated/60 border-neon-yellow/30 opacity-60 cursor-not-allowed"
                      : "bg-elevated border-neon-green/25 hover:border-neon-green"
                  }`}
                >
                  {soldOut && (
                    <span className="absolute top-1.5 right-1.5 z-10 bg-neon-yellow text-black font-heading text-[10px] uppercase tracking-widest px-1.5 py-0.5">
                      Sold Out
                    </span>
                  )}
                  {images[it.id] && (
                    <div className="w-full aspect-[4/3] bg-black overflow-hidden -mx-3 -mt-3 mb-2">
                      <img src={images[it.id].startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${images[it.id]}` : images[it.id]} alt="" className={`w-full h-full object-cover ${soldOut ? "grayscale" : ""}`} loading="lazy" />
                    </div>
                  )}
                  <span className={`font-heading text-lg uppercase tracking-wide leading-tight ${soldOut ? "text-neutral-500" : "text-white"}`}>{it.name}</span>
                  <span className="font-heading text-lg text-neon-yellow mt-auto">
                    {it.variants ? `from ${money(price)}` : money(price)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Cart + Payment */}
        <aside className="border-t lg:border-t-0 lg:border-l border-neon-green/25 bg-black flex flex-col max-h-[70vh] lg:max-h-none">
          <div className="p-4 border-b border-neon-green/15">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl text-neon-green uppercase tracking-wide">Order</h2>
              {items.length > 0 && (
                <button onClick={clear} data-testid="pos-clear-btn" className="font-body text-xs uppercase tracking-widest text-neutral-500 hover:text-red-500 inline-flex items-center gap-1">
                  <Trash2 size={12} /> Clear
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4" data-testid="pos-cart">
            {items.length === 0 ? (
              <p className="font-body text-neutral-600 text-center pt-8">Tap items to build the order.</p>
            ) : (
              <div className="space-y-2">
                {items.map((it) => (
                  <div key={it.lineId} className="border border-neon-green/15 bg-elevated p-3">
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-heading text-sm uppercase text-white tracking-wide truncate">{it.quantity}× {it.name}</div>
                        {(it.options || []).length > 0 && (
                          <div className="font-body text-[11px] text-neutral-500 mt-0.5 line-clamp-2">{it.options.join(" · ")}</div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-heading text-neon-yellow">{money(it.unit_price_cents * it.quantity)}</div>
                        <button onClick={() => removeItem(it.lineId)} data-testid={`pos-remove-${it.lineId}`} className="text-neutral-600 hover:text-red-500 mt-1">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-neon-green/25 p-4 space-y-3 bg-elevated">
            <div className="flex items-baseline justify-between">
              <span className="font-body text-xs uppercase tracking-[0.2em] text-neutral-400">Total</span>
              <span className="font-heading text-4xl text-neon-green" data-testid="pos-total">{money(totalCents)}</span>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="font-body text-[11px] uppercase tracking-widest text-neutral-500 block mb-1">Cash Tendered</label>
                <div className="flex items-center gap-1 border border-neon-green/30 bg-black px-2">
                  <DollarSign size={14} className="text-neutral-500" />
                  <input
                    value={tender}
                    onChange={(e) => setTender(e.target.value.replace(/[^0-9.]/g, ""))}
                    inputMode="decimal"
                    placeholder="0.00"
                    data-testid="pos-tender-input"
                    className="w-full bg-transparent text-white font-heading text-lg py-2 focus:outline-none text-right"
                  />
                </div>
              </div>
              <div>
                <label className="font-body text-[11px] uppercase tracking-widest text-neutral-500 block mb-1">Change Due</label>
                <div data-testid="pos-change" className="border border-neon-yellow/30 bg-black px-2 py-2 font-heading text-lg text-neon-yellow text-right">
                  {money(change)}
                </div>
              </div>
            </div>

            {/* Quick tender chips */}
            <div className="flex flex-wrap gap-1.5">
              {[500, 1000, 2000, 5000, 10000].map((c) => (
                <button
                  key={c}
                  onClick={() => setTender((c / 100).toFixed(2))}
                  data-testid={`pos-quick-${c}`}
                  className="font-heading text-xs uppercase tracking-wide border border-neon-green/30 text-neon-green px-2.5 py-1 hover:bg-neon-green hover:text-black transition-colors"
                >${c / 100}</button>
              ))}
              <button
                onClick={() => setTender((totalCents / 100).toFixed(2))}
                data-testid="pos-quick-exact"
                className="font-heading text-xs uppercase tracking-wide border border-neon-yellow/40 text-neon-yellow px-2.5 py-1 hover:bg-neon-yellow hover:text-black transition-colors"
              >Exact</button>
            </div>

            <Button
              onClick={charge}
              disabled={!canCharge || busy}
              data-testid="pos-charge-btn"
              className="w-full font-heading text-2xl bg-neon-green text-black hover:bg-neon-yellow py-6 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {busy ? <Loader2 className="animate-spin mr-2" /> : <CheckCircle2 className="mr-2" />}
              Charge Cash
            </Button>
          </div>
        </aside>
      </div>

      <ItemDialog
        item={active}
        open={!!active}
        onClose={() => setActive(null)}
        flavors={flavors}
        sodas={sodas}
        dips={dips}
        comboPrice={comboPrice}
      />

      <ManualKeypad open={manualOpen} onClose={() => setManualOpen(false)} onCharge={chargeManual} busy={busy} />

      <AnimatePresence>
        {receipt && (
          <ReceiptModal receipt={receipt} onClose={() => setReceipt(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

function ManualKeypad({ open, onClose, onCharge, busy }) {
  const [amountCents, setAmountCents] = useState(0);
  const [tenderStr, setTenderStr] = useState("");
  const [label, setLabel] = useState("Manual Sale");

  useEffect(() => { if (open) { setAmountCents(0); setTenderStr(""); setLabel("Manual Sale"); } }, [open]);

  const pushDigit = (d) => setAmountCents((c) => Math.min(9999999, c * 10 + d));
  const backspace = () => setAmountCents((c) => Math.floor(c / 10));
  const tenderCents = centsFromInput(tenderStr);
  const change = Math.max(0, tenderCents - amountCents);

  const submit = () => {
    if (amountCents <= 0) return;
    onCharge({ amountCents, tenderCents: tenderCents || amountCents, label });
  };

  const Key = ({ children, onClick, testid, className = "" }) => (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`font-heading text-3xl bg-elevated border border-neon-green/30 text-white h-16 hover:bg-neon-green/10 active:bg-neon-green active:text-black transition-colors ${className}`}
    >{children}</button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-elevated border-neon-green/30 text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl text-neon-green uppercase tracking-wide flex items-center gap-2">
            <Calculator size={24} /> Manual Sale
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="What is this for? (optional)"
            data-testid="manual-label"
            className="w-full bg-black border border-neon-green/25 text-white font-body px-3 py-2 focus:outline-none focus:border-neon-green"
          />
          <div className="bg-black border border-neon-green/30 p-4 text-right">
            <span className="font-body text-[10px] uppercase tracking-[0.2em] text-neutral-500 block">Amount</span>
            <span data-testid="manual-amount" className="font-heading text-5xl text-neon-yellow leading-none">
              ${(amountCents / 100).toFixed(2)}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <Key key={n} testid={`manual-key-${n}`} onClick={() => pushDigit(n)}>{n}</Key>
            ))}
            <Key testid="manual-key-00" onClick={() => { pushDigit(0); pushDigit(0); }}>00</Key>
            <Key testid="manual-key-0" onClick={() => pushDigit(0)}>0</Key>
            <Key testid="manual-key-back" onClick={backspace} className="text-red-400"><Delete size={22} className="mx-auto" /></Key>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <div>
              <label className="font-body text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-1">Tender</label>
              <input
                value={tenderStr}
                onChange={(e) => setTenderStr(e.target.value.replace(/[^0-9.]/g, ""))}
                inputMode="decimal"
                placeholder="0.00"
                data-testid="manual-tender"
                className="w-full bg-black border border-neon-green/30 text-white font-heading text-lg px-2 py-2 text-right focus:outline-none focus:border-neon-green"
              />
            </div>
            <div>
              <label className="font-body text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-1">Change</label>
              <div data-testid="manual-change" className="bg-black border border-neon-yellow/30 px-2 py-2 font-heading text-lg text-neon-yellow text-right">
                ${(change / 100).toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={submit}
            disabled={amountCents <= 0 || busy}
            data-testid="manual-charge-btn"
            className="w-full font-heading text-xl bg-neon-green text-black hover:bg-neon-yellow py-5 disabled:opacity-30"
          >
            {busy ? <Loader2 className="animate-spin mr-2" /> : <CheckCircle2 className="mr-2" />}
            Charge Cash
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReceiptModal({ receipt, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center px-6"
    >
      <motion.div
        initial={{ scale: 0.85, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="pos-receipt"
        className="w-full max-w-sm border border-neon-green bg-elevated p-8 text-center relative"
      >
        <div className="w-16 h-16 rounded-full bg-neon-green/10 border border-neon-green mx-auto flex items-center justify-center">
          <CheckCircle2 size={34} className="text-neon-green" />
        </div>
        <h2 className="font-heading text-3xl text-neon-green uppercase tracking-wide mt-4">Paid</h2>
        <div className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mt-4">Order Number</div>
        <div className="font-heading text-7xl text-neon-yellow leading-none neon-glow-yellow" data-testid="pos-receipt-num">#{receipt.order_number}</div>

        <div className="grid grid-cols-3 gap-2 mt-6 text-left">
          <div className="border border-neon-green/20 bg-black p-2">
            <span className="font-body text-[9px] uppercase tracking-widest text-neutral-500 block">Total</span>
            <span className="font-heading text-lg text-white">${(receipt.total_cents / 100).toFixed(2)}</span>
          </div>
          <div className="border border-neon-green/20 bg-black p-2">
            <span className="font-body text-[9px] uppercase tracking-widest text-neutral-500 block">Tendered</span>
            <span className="font-heading text-lg text-white">${(receipt.tender_cents / 100).toFixed(2)}</span>
          </div>
          <div className="border border-neon-yellow/40 bg-black p-2">
            <span className="font-body text-[9px] uppercase tracking-widest text-neutral-500 block">Change</span>
            <span className="font-heading text-lg text-neon-yellow">${(receipt.change_cents / 100).toFixed(2)}</span>
          </div>
        </div>

        <Button onClick={onClose} data-testid="pos-receipt-close" className="mt-6 w-full font-heading text-lg bg-neon-green text-black hover:bg-neon-yellow py-4">
          <Plus size={16} className="mr-2" /> New Order
        </Button>
        <p className="font-body text-[10px] text-neutral-600 uppercase tracking-widest mt-2">Auto-closes in 6s</p>
      </motion.div>
    </motion.div>
  );
}
